/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Landmark, ShieldCheck, KeyRound, Workflow, ListChecks, FileCode2,
  Sparkles, ExternalLink, Loader2, RefreshCw, Save, ChevronDown,
  ChevronRight, Copy, Trash2, AlertTriangle, CheckCircle2,
  CircleDashed, FlaskConical, Rocket, Building2, FileWarning, Plus,
  Lock,
} from "lucide-react";

/**
 * ATO Access — Digital Service Provider (DSP) onboarding +
 * integration scaffold. Captures every piece of registration state,
 * credentials and OSF compliance the user needs before they can
 * transmit pay events / stapled super requests to production.
 *
 * No live transmission code lives here yet — that comes once the
 * user is whitelisted via PVT. This page is the *framework*.
 */
export default function AtoAccessPage() {
  const [cfg, setCfg] = useState(null);
  const [journey, setJourney] = useState(null);
  const [osf, setOsf] = useState(null);
  const [softwareIds, setSoftwareIds] = useState([]);
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const [c, j, o, s, r] = await Promise.all([
        api.get("/integrations/ato/config"),
        api.get("/integrations/ato/journey"),
        api.get("/integrations/ato/osf-controls"),
        api.get("/integrations/ato/software-ids"),
        api.get("/integrations/ato/reference"),
      ]);
      setCfg(c.data); setJourney(j.data); setOsf(o.data);
      setSoftwareIds(s.data.items || []); setReference(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load ATO config");
    }
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await loadAll(); setLoading(false); })();
  }, [loadAll]);

  if (loading || !cfg) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading ATO config…
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-[1280px]"
      data-testid="ato-access-page">
      <PageHeader cfg={cfg} journey={journey} onReload={loadAll} />
      <JourneyCard journey={journey} onToggle={async (id, done) => {
        await api.put("/integrations/ato/config",
          { registration: { [id]: done } });
        loadAll();
      }} />
      <BusinessCard cfg={cfg} onSaved={loadAll} />
      <PathwaysCard />
      <CredentialsCard cfg={cfg} onSaved={loadAll} />
      <OsfCard cfg={cfg} osf={osf} onSaved={loadAll} />
      <SoftwareIdsCard items={softwareIds} onChanged={loadAll} />
      <ReferenceCard reference={reference} />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Header — title + progress bar + active env switcher.
 * ────────────────────────────────────────────────────────────────── */
function PageHeader({ cfg, journey, onReload }) {
  const pct = journey?.percent ?? 0;
  const env = cfg.active_environment;
  const isProd = env === "production";

  const switchEnv = async (next) => {
    if (next === env) return;
    if (next === "production" && !cfg.registration?.pvt_passed) {
      if (!window.confirm(
        "PVT hasn't been marked as passed yet. Are you sure you want " +
        "to switch the active environment to PRODUCTION?")) return;
    }
    try {
      await api.put("/integrations/ato/config", { active_environment: next });
      onReload();
      toast.success(`Switched to ${next.toUpperCase()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="pb-4 border-b border-zinc-200" data-testid="ato-header">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-amber-100 text-amber-800">
          <Landmark className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="label-overline">FINANCE &amp; PAYROLL · ATO ACCESS</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-0.5">
            ATO Digital Service Provider
          </h1>
          <p className="text-xs text-zinc-600 mt-1 max-w-3xl">
            Onboarding scaffold for integrating with the Australian Taxation
            Office. Build out the DSP requirements below — once you've registered
            on the
            <a href="https://developer.ato.gov.au/" target="_blank" rel="noreferrer"
              className="text-amber-800 underline mx-1">ATO API Portal<ExternalLink className="h-3 w-3 inline ml-0.5" /></a>
            (modern REST/JSON) and/or
            <a href="https://softwaredevelopers.ato.gov.au/" target="_blank" rel="noreferrer"
              className="text-amber-800 underline mx-1">Online services for DSPs<ExternalLink className="h-3 w-3 inline ml-0.5" /></a>
            (SBR for STP), paste your credentials here and we'll wire the live
            transmission code.
          </p>
        </div>

        <div className="flex flex-col items-end gap-2">
          {/* Env switcher */}
          <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5"
            data-testid="ato-env-toggle">
            {[
              { v: "evte", label: "EVTE", icon: FlaskConical },
              { v: "production", label: "Production", icon: Rocket },
            ].map((o) => {
              const active = env === o.v;
              const isProdBtn = o.v === "production";
              return (
                <button key={o.v} onClick={() => switchEnv(o.v)}
                  data-testid={`ato-env-${o.v}`}
                  className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-sm font-display font-bold text-[11px] uppercase tracking-wider transition ${
                    active
                      ? (isProdBtn ? "bg-emerald-600 text-white" : "bg-zinc-900 text-white")
                      : "text-zinc-600 hover:bg-zinc-50"
                  }`}>
                  <o.icon className="h-3 w-3" /> {o.label}
                </button>
              );
            })}
          </div>
          <Button size="sm" variant="outline" className="rounded-sm h-7"
            onClick={onReload} data-testid="ato-reload">
            <RefreshCw className="h-3 w-3 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-3 items-center">
        <div>
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700 mb-1.5">
            <span>Onboarding progress</span>
            <span className="font-mono text-zinc-500 normal-case tracking-normal">
              {journey?.completed ?? 0}/{journey?.total ?? 10} · {pct}%
            </span>
          </div>
          <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden"
            data-testid="ato-progress-bar">
            <div className={`h-full transition-all duration-500 ${
              pct < 30 ? "bg-amber-500" : pct < 70 ? "bg-blue-500" : "bg-emerald-500"
            }`} style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className={`text-center px-3 py-1.5 rounded-sm border text-[11px] font-display font-bold uppercase tracking-[0.15em] ${
          isProd ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                 : "border-amber-300 bg-amber-50 text-amber-800"
        }`} data-testid="ato-active-env-pill">
          Active env: {env}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Journey — 10 onboarding steps with checkbox toggles + helpful
 * external links pulled from the spec.
 * ────────────────────────────────────────────────────────────────── */
function JourneyCard({ journey, onToggle }) {
  if (!journey) return null;
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ato-journey-card">
      <CardContent className="p-6 space-y-3">
        <SectionHead icon={Workflow} title="The journey"
          sub="Tick steps off as you complete them — purely for your own tracking. None of these checkboxes call the ATO." />
        <ol className="space-y-2 ml-2">
          {journey.steps.map((s, idx) => (
            <li key={s.id} className="flex items-start gap-2"
              data-testid={`ato-journey-step-${s.id}`}>
              <button onClick={() => onToggle(s.id, !s.done)}
                className={`mt-0.5 shrink-0 h-5 w-5 grid place-items-center rounded-sm border transition ${
                  s.done ? "bg-emerald-600 border-emerald-600 text-white"
                         : "bg-white border-zinc-300 hover:border-zinc-500"
                }`}
                data-testid={`ato-journey-toggle-${s.id}`}>
                {s.done ? <CheckCircle2 className="h-3.5 w-3.5" />
                        : <span className="text-[10px] font-mono text-zinc-400">{idx + 1}</span>}
              </button>
              <div className="flex-1">
                <div className={`text-sm ${s.done ? "text-zinc-500 line-through" : "text-zinc-900"}`}>
                  {s.label}
                </div>
                {(s.where || s.url) && (
                  <div className="text-[10px] text-zinc-500 mt-0.5">
                    {s.where && <span className="font-mono">{s.where}</span>}
                    {s.url && (
                      <a href={s.url} target="_blank" rel="noreferrer"
                        className="text-amber-800 underline ml-2 inline-flex items-center gap-1">
                        Open <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Business info — ABN, entity name, DPO contact, OSF category override.
 * ────────────────────────────────────────────────────────────────── */
function BusinessCard({ cfg, onSaved }) {
  const [draft, setDraft] = useState(cfg.business || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(cfg.business || {}), [cfg.business]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/integrations/ato/config", { business: draft });
      toast.success("Business info saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ato-business-card">
      <CardContent className="p-6 space-y-3">
        <SectionHead icon={Building2} title="Business entity"
          sub="Your registered ABN + entity name that will be quoted in DSP submissions." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="label-overline">ABN (11 digits)</Label>
            <Input className="rounded-sm font-mono" maxLength={11}
              value={draft.abn || ""}
              onChange={(e) => setDraft((d) => ({ ...d, abn: e.target.value.replace(/\D/g, "") }))}
              data-testid="ato-business-abn" />
          </div>
          <div className="space-y-1">
            <Label className="label-overline">Entity name</Label>
            <Input className="rounded-sm" value={draft.entity_name || ""}
              onChange={(e) => setDraft((d) => ({ ...d, entity_name: e.target.value }))}
              data-testid="ato-business-entity" />
          </div>
          <div className="space-y-1">
            <Label className="label-overline">DPO contact email</Label>
            <Input className="rounded-sm" type="email"
              placeholder="dpo@example.com.au" value={draft.dpo_contact_email || ""}
              onChange={(e) => setDraft((d) => ({ ...d, dpo_contact_email: e.target.value }))}
              data-testid="ato-business-dpo-email" />
          </div>
          <div className="space-y-1">
            <Label className="label-overline">ATO API Portal team name</Label>
            <Input className="rounded-sm" value={draft.team_name || ""}
              onChange={(e) => setDraft((d) => ({ ...d, team_name: e.target.value }))}
              data-testid="ato-business-team-name" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving}
            className="rounded-sm bg-amber-700 hover:bg-amber-800"
            data-testid="ato-business-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                    : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save
          </Button>
          {cfg.bms_id && (
            <span className="text-[10px] font-mono text-zinc-500">
              BMS ID (auto): {cfg.bms_id}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Pathways — REST/JSON vs SBR explainer cards.
 * ────────────────────────────────────────────────────────────────── */
function PathwaysCard() {
  const pathways = [
    {
      key: "rest",
      title: "REST / JSON — ATO API Portal",
      desc: "Modern endpoints. Stapled Super Fund, Dynamic Client Registration, specialized tax forms. OAuth 2.0 client credentials + scoped tokens.",
      register: "developer.ato.gov.au",
      url: "https://developer.ato.gov.au/",
      icon: FileCode2,
      examples: ["ato.super.stapledsuperfund.c,r", "ato.dcr"],
    },
    {
      key: "sbr",
      title: "SBR — Standard Business Reporting",
      desc: "Heavy XML/XBRL. Required for STP Phase 2 pay events + core corporate tax returns. Registration via DPO; Product ID assigned post-certification.",
      register: "softwaredevelopers.ato.gov.au",
      url: "https://softwaredevelopers.ato.gov.au/",
      icon: FileWarning,
      examples: ["STP Pay Events (Submit + Update)", "PAYG / BAS returns"],
    },
  ];
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ato-pathways-card">
      <CardContent className="p-6 space-y-3">
        <SectionHead icon={Workflow} title="Pathways"
          sub="Most platforms need BOTH. The REST channel handles employee-onboarding lookups; SBR handles every pay run." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pathways.map((p) => (
            <div key={p.key} className="border border-zinc-200 rounded-sm p-4 bg-white"
              data-testid={`ato-pathway-${p.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <p.icon className="h-4 w-4 text-amber-800" />
                <h3 className="font-display font-bold text-sm">{p.title}</h3>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed mb-3">{p.desc}</p>
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700 mb-1">
                Examples
              </div>
              <ul className="text-[11px] font-mono text-zinc-600 list-disc list-inside mb-3">
                {p.examples.map((ex) => <li key={ex}>{ex}</li>)}
              </ul>
              <a href={p.url} target="_blank" rel="noreferrer"
                className="text-amber-800 text-xs underline inline-flex items-center gap-1">
                Register on {p.register} <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Credentials vault — EVTE + Production OAuth + Product ID.
 * ────────────────────────────────────────────────────────────────── */
function CredentialsCard({ cfg, onSaved }) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ato-credentials-card">
      <CardContent className="p-6 space-y-4">
        <SectionHead icon={KeyRound} title="OAuth credentials"
          sub="Paste your OAuth 2.0 client credentials from the ATO API Portal Team Application. EVTE first; Production once you've passed PVT." />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CredsBlock env="evte" label="EVTE (test)"
            data={cfg.credentials.evte}
            accent="bg-amber-50 border-amber-200"
            onSaved={onSaved} />
          <CredsBlock env="production" label="Production (live)"
            data={cfg.credentials.production}
            accent="bg-emerald-50 border-emerald-200"
            onSaved={onSaved} />
        </div>
        <ConnectionTestPanel cfg={cfg} onTested={onSaved} />
      </CardContent>
    </Card>
  );
}

function CredsBlock({ env, label, data, accent, onSaved }) {
  const [draft, setDraft] = useState({ oauth_client_id: data.client_id || "",
                                        oauth_client_secret: "",
                                        product_id: data.product_id || "",
                                        base_url: data.base_url || "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDraft({ oauth_client_id: data.client_id || "",
               oauth_client_secret: "",
               product_id: data.product_id || "",
               base_url: data.base_url || "" });
  }, [data.client_id, data.product_id, data.base_url]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/integrations/ato/credentials", { env, ...draft });
      toast.success(`${label} saved`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const clear = async () => {
    if (!window.confirm(`Clear ${label} credentials? You'll need to re-enter them.`)) return;
    try {
      await api.delete(`/integrations/ato/credentials/${env}`);
      toast.success(`${label} cleared`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Clear failed"); }
  };
  return (
    <div className={`rounded-sm border p-4 ${accent}`} data-testid={`ato-creds-${env}`}>
      <div className="flex items-center gap-2 mb-3">
        <h4 className="font-display font-bold text-sm">{label}</h4>
        {data.configured ? (
          <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 ml-auto">
            <CheckCircle2 className="h-3 w-3 mr-1" /> configured
          </Badge>
        ) : (
          <Badge variant="outline" className="rounded-sm text-zinc-500 border-zinc-300 ml-auto">
            <CircleDashed className="h-3 w-3 mr-1" /> empty
          </Badge>
        )}
      </div>
      <div className="space-y-2">
        <div>
          <Label className="label-overline text-[9px]">OAuth client_id</Label>
          <Input className="rounded-sm h-8 text-xs font-mono"
            value={draft.oauth_client_id}
            onChange={(e) => setDraft((d) => ({ ...d, oauth_client_id: e.target.value }))}
            data-testid={`ato-creds-${env}-client-id`} />
        </div>
        <div>
          <Label className="label-overline text-[9px]">OAuth client_secret</Label>
          <Input className="rounded-sm h-8 text-xs font-mono" type="password"
            placeholder={data.configured ? "•••• saved — blank = keep" : ""}
            value={draft.oauth_client_secret}
            onChange={(e) => setDraft((d) => ({ ...d, oauth_client_secret: e.target.value }))}
            data-testid={`ato-creds-${env}-client-secret`} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="label-overline text-[9px]">Product ID</Label>
            <Input className="rounded-sm h-8 text-xs font-mono"
              placeholder="ATO-assigned"
              value={draft.product_id}
              onChange={(e) => setDraft((d) => ({ ...d, product_id: e.target.value }))}
              data-testid={`ato-creds-${env}-product-id`} />
          </div>
          <div>
            <Label className="label-overline text-[9px]">Base URL</Label>
            <Input className="rounded-sm h-8 text-xs font-mono"
              value={draft.base_url}
              onChange={(e) => setDraft((d) => ({ ...d, base_url: e.target.value }))}
              data-testid={`ato-creds-${env}-base-url`} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" onClick={save} disabled={saving}
          className="rounded-sm bg-amber-700 hover:bg-amber-800"
          data-testid={`ato-creds-${env}-save`}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  : <Save className="h-3.5 w-3.5 mr-1.5" />}
          Save
        </Button>
        {data.configured && (
          <Button size="sm" variant="outline" className="rounded-sm"
            onClick={clear}
            data-testid={`ato-creds-${env}-clear`}>
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Clear
          </Button>
        )}
      </div>
    </div>
  );
}

function ConnectionTestPanel({ cfg, onTested }) {
  const [busy, setBusy] = useState(false);
  const env = cfg.active_environment;
  const envData = cfg.credentials[env];
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/ato/test");
      (data?.ok ? toast.success : toast.error)(
        `Probe ${data?.env?.toUpperCase()} → ${data?.status_code ?? "n/a"}`);
      onTested();
    } catch (e) { toast.error(e.response?.data?.detail || "Probe failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-dashed border-zinc-200 rounded-sm p-3 bg-zinc-50/50">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="h-3.5 w-3.5 text-amber-700" />
        <span className="text-xs font-semibold">
          Probe the active <span className="font-mono uppercase">{env}</span> environment
        </span>
        <Button size="sm" variant="outline" className="rounded-sm ml-auto"
          onClick={test} disabled={busy || !envData?.configured}
          data-testid="ato-test-connection">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Test connection
        </Button>
      </div>
      {cfg.last_test_status != null && (
        <div className="text-[10px] font-mono text-zinc-500 mt-2 leading-relaxed">
          last probe HTTP {cfg.last_test_status}
          {cfg.last_test_at && <> · {cfg.last_test_at.slice(0,16).replace("T", " ")}</>}
          {cfg.last_test_message && (
            <div className="mt-0.5 text-zinc-600 break-all">
              {cfg.last_test_message.slice(0, 200)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * OSF compliance — 5 mandatory controls + category override.
 * ────────────────────────────────────────────────────────────────── */
function OsfCard({ cfg, osf, onSaved }) {
  const toggleControl = async (id, v) => {
    await api.put("/integrations/ato/config",
      { osf_controls: { [id]: v } });
    onSaved();
  };
  const setHosting = async (v) => {
    await api.put("/integrations/ato/config", { hosting_model: v });
    onSaved();
  };
  const setVolume = async (v) => {
    await api.put("/integrations/ato/config", { data_volume_over_10k: v });
    onSaved();
  };
  const setCategory = async (v) => {
    await api.put("/integrations/ato/config",
      { business: { osf_category: v } });
    onSaved();
  };

  if (!osf) return null;
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ato-osf-card">
      <CardContent className="p-6 space-y-4">
        <SectionHead icon={ShieldCheck}
          title="Operational Security Framework (OSF)"
          sub="Mandatory before you hit live data endpoints. Requirements scale Category A → E by data volume + hosting model." />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-zinc-200 rounded-sm p-3 bg-white">
            <Label className="label-overline mb-1.5">Hosting model</Label>
            <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5">
              {[{ v: "saas", l: "SaaS (cloud)" }, { v: "desktop", l: "Desktop" }].map((o) => (
                <button key={o.v} onClick={() => setHosting(o.v)}
                  data-testid={`ato-osf-hosting-${o.v}`}
                  className={`px-2.5 h-7 rounded-sm text-[10px] font-display font-bold uppercase tracking-wider ${
                    cfg.hosting_model === o.v ? "bg-zinc-900 text-white" : "text-zinc-600"
                  }`}>{o.l}</button>
              ))}
            </div>
          </div>
          <div className="border border-zinc-200 rounded-sm p-3 bg-white">
            <Label className="label-overline mb-1.5">Data volume</Label>
            <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5">
              {[{ v: false, l: "< 10k" }, { v: true, l: "≥ 10k" }].map((o) => (
                <button key={String(o.v)} onClick={() => setVolume(o.v)}
                  data-testid={`ato-osf-volume-${o.v}`}
                  className={`px-2.5 h-7 rounded-sm text-[10px] font-display font-bold uppercase tracking-wider ${
                    cfg.data_volume_over_10k === o.v ? "bg-zinc-900 text-white" : "text-zinc-600"
                  }`}>{o.l}</button>
              ))}
            </div>
          </div>
          <div className="border border-zinc-200 rounded-sm p-3 bg-white">
            <Label className="label-overline mb-1.5">OSF category</Label>
            <div className="inline-flex rounded-sm border border-zinc-200 bg-white p-0.5">
              {["A", "B", "C", "D", "E"].map((c) => (
                <button key={c} onClick={() => setCategory(c)}
                  data-testid={`ato-osf-cat-${c}`}
                  className={`w-7 h-7 rounded-sm text-[11px] font-display font-bold ${
                    osf.category === c ? "bg-amber-700 text-white" : "text-zinc-600 hover:bg-zinc-50"
                  }`}>{c}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-zinc-600 italic px-1">
          <AlertTriangle className="h-3 w-3 inline mr-1 text-amber-600" />
          Suggested category from your hosting + volume: <span className="font-semibold not-italic">{osf.category_hint}</span>
        </div>

        <div className="space-y-1.5">
          {osf.controls.map((c) => (
            <div key={c.id}
              className="flex items-center gap-3 px-3 py-2 border border-zinc-200 rounded-sm bg-white"
              data-testid={`ato-osf-control-${c.id}`}>
              <button onClick={() => toggleControl(c.id, !c.user_attested)}
                data-testid={`ato-osf-control-toggle-${c.id}`}
                className={`shrink-0 h-5 w-5 grid place-items-center rounded-sm border transition ${
                  c.user_attested ? "bg-emerald-600 border-emerald-600 text-white"
                                  : "bg-white border-zinc-300 hover:border-zinc-500"
                }`}>
                {c.user_attested && <CheckCircle2 className="h-3.5 w-3.5" />}
              </button>
              <span className="flex-1 text-sm">{c.label}</span>
              <PlatformPill status={c.platform_status} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PlatformPill({ status }) {
  const cfg = {
    platform_ok: { label: "Platform OK", c: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    available: { label: "Available", c: "bg-blue-50 text-blue-800 border-blue-200" },
    user_must_attest: { label: "You attest", c: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  }[status] || { label: status, c: "bg-zinc-100" };
  return (
    <Badge variant="outline" className={`rounded-sm text-[9px] font-display font-bold uppercase tracking-[0.12em] ${cfg.c}`}>
      {cfg.label}
    </Badge>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Software IDs — one per business client. Generated + revocable.
 * ────────────────────────────────────────────────────────────────── */
function SoftwareIdsCard({ items, onChanged }) {
  const [clientId, setClientId] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!clientId.trim()) return toast.error("Client ID required");
    setCreating(true);
    try {
      const { data } = await api.post("/integrations/ato/software-ids",
        { client_id: clientId.trim(), notes: notes.trim() || null });
      toast.success(data.reused
        ? `Re-used existing Software ID ${data.item.software_id}`
        : `Generated Software ID ${data.item.software_id}`);
      setClientId(""); setNotes(""); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };
  const revoke = async (id) => {
    if (!window.confirm(`Revoke Software ID ${id}? The client will no longer be able to transmit through this platform.`)) return;
    try {
      await api.delete(`/integrations/ato/software-ids/${id}`);
      toast.success("Revoked");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copied");
  };

  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ato-software-ids-card">
      <CardContent className="p-6 space-y-3">
        <SectionHead icon={Lock} title="Software IDs"
          sub="Each commercial client gets a unique Software ID that they paste into their ATO Access Manager portal to authorise this platform to transmit on their behalf." />

        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <Input className="rounded-sm" placeholder="Client ID (e.g. client_xxxxx)"
            value={clientId} onChange={(e) => setClientId(e.target.value)}
            data-testid="ato-software-id-client" />
          <Input className="rounded-sm" placeholder="Notes (optional)"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            data-testid="ato-software-id-notes" />
          <Button size="sm" onClick={create} disabled={creating}
            className="rounded-sm bg-amber-700 hover:bg-amber-800"
            data-testid="ato-software-id-generate">
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            Generate
          </Button>
        </div>

        {items.length === 0 ? (
          <div className="text-xs italic text-zinc-500 py-4 text-center">
            No Software IDs issued yet.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
            {items.map((it) => (
              <div key={it.software_id}
                className={`grid grid-cols-[150px_1fr_1fr_70px_auto] gap-3 px-3 py-2 items-center text-sm ${
                  !it.active ? "opacity-50" : ""
                }`}
                data-testid={`ato-software-id-row-${it.software_id}`}>
                <span className="font-mono font-semibold">{it.software_id}</span>
                <span className="font-mono text-[11px] text-zinc-600 truncate" title={it.client_id}>
                  {it.client_id}
                </span>
                <span className="text-[10px] text-zinc-500 truncate" title={it.notes || ""}>
                  {it.notes || <em className="text-zinc-400">no notes</em>}
                </span>
                <Badge variant="outline"
                  className={`rounded-sm text-[9px] text-center ${
                    it.active ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : "bg-zinc-100 text-zinc-500"}`}>
                  {it.active ? "Active" : "Revoked"}
                </Badge>
                <div className="flex items-center gap-1 justify-end">
                  <Button size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => copy(it.software_id)}
                    title="Copy Software ID"
                    data-testid={`ato-software-id-copy-${it.software_id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  {it.active && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600"
                      onClick={() => revoke(it.software_id)}
                      title="Revoke"
                      data-testid={`ato-software-id-revoke-${it.software_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Reference — STP Phase 2 schema tree + Tax Treatment Code builder
 * + sample Stapled Super Fund payload + scopes catalog.
 * ────────────────────────────────────────────────────────────────── */
function ReferenceCard({ reference }) {
  const [open, setOpen] = useState(false);
  if (!reference) return null;
  return (
    <Card className="rounded-sm border-zinc-200" data-testid="ato-reference-card">
      <CardContent className="p-6 space-y-3">
        <button onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center gap-2 text-left"
          data-testid="ato-reference-toggle">
          <ListChecks className="h-4 w-4 text-zinc-700" />
          <span className="font-display font-bold text-lg tracking-tight">Reference</span>
          <span className="text-[11px] text-zinc-500 ml-2">STP schema · Tax Treatment Code builder · sample payloads · scopes catalog</span>
          {open ? <ChevronDown className="h-4 w-4 ml-auto" />
                : <ChevronRight className="h-4 w-4 ml-auto" />}
        </button>
        {open && (
          <div className="space-y-4 pt-2">
            <ScopesCatalog scopes={reference.scopes_catalog} />
            <TaxTreatmentBuilder dict={reference.tax_treatment_code_dict} />
            <PayEventTree tree={reference.pay_event_tree} />
            <SamplePayload payload={reference.stapled_super_sample} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScopesCatalog({ scopes }) {
  return (
    <div className="border border-zinc-200 rounded-sm">
      <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700">
        Scopes catalog
      </div>
      <div className="divide-y divide-zinc-100">
        {scopes.map((s) => (
          <div key={s.scope}
            className="grid grid-cols-[1fr_120px_180px] gap-3 px-3 py-2 text-xs items-center">
            <div>
              <div className="font-mono">{s.scope}</div>
              <div className="text-[10px] text-zinc-500">{s.label}</div>
            </div>
            <Badge variant="outline" className="rounded-sm text-[9px] justify-self-start">
              {s.channel}
            </Badge>
            <span className="text-[10px] text-zinc-600">{s.risk}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TaxTreatmentBuilder({ dict }) {
  const [code, setCode] = useState({
    char1_category: "R", char2_options: "T", char3_study_loans: "X",
    char4_medicare_surcharge: "X", char5_medicare_exemption: "X",
    char6_variations: "S",
  });
  const combined = useMemo(() =>
    `${code.char1_category}${code.char2_options}${code.char3_study_loans}${code.char4_medicare_surcharge}${code.char5_medicare_exemption}${code.char6_variations}`,
    [code]);
  return (
    <div className="border border-zinc-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700 mb-2">
        6-character Tax Treatment Code builder
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        {Object.entries(dict).map(([key, options]) => (
          <div key={key}>
            <Label className="label-overline text-[9px]">
              {key.replace(/^char\d+_/, "").replace(/_/g, " ")}
            </Label>
            <select className="w-full rounded-sm border border-zinc-200 h-8 text-xs px-1.5 bg-white"
              value={code[key]} onChange={(e) => setCode((c) => ({ ...c, [key]: e.target.value }))}
              data-testid={`ato-ttc-${key}`}>
              {options.map((o) => (
                <option key={o.v} value={o.v}>{o.v} — {o.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <div className="font-mono text-2xl font-bold text-amber-800 tracking-[0.25em] bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 inline-block"
        data-testid="ato-ttc-result">
        {combined}
      </div>
    </div>
  );
}

function PayEventTree({ tree }) {
  // Flatten the recursive tree into a list of {label, values, depth}
  // entries — recursive React components confuse the emergent visual
  // editor's Babel plugin, so we walk the tree iteratively here.
  const rows = [];
  const walk = (nodes, depth) => {
    nodes.forEach((n) => {
      rows.push({ label: n.label, values: n.values, depth });
      if (n.children) walk(n.children, depth + 1);
    });
  };
  walk(tree, 1);
  return (
    <div className="border border-zinc-200 rounded-sm p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700 mb-2">
        STP Phase 2 pay event — disaggregation tree (mandatory)
      </div>
      <div className="font-mono text-[11px] leading-relaxed text-zinc-700">
        <div className="font-bold">[STP Pay Event Root]</div>
        {rows.map((r) => (
          <div key={`${r.depth}-${r.label}`}
            style={{ paddingLeft: `${(r.depth - 1) * 14}px` }}>
            {"├── "}
            <span className="text-amber-800 font-semibold">{r.label}</span>
            {r.values && (
              <span className="text-zinc-500">
                {" ("}
                {r.values.slice(0, 3).join(" · ")}
                {r.values.length > 3 ? " · …" : ""}
                {")"}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
function SamplePayload({ payload }) {
  return (
    <div className="border border-zinc-200 rounded-sm">
      <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-700 flex items-center">
        Stapled Super Fund — sample POST payload
        <Button size="icon" variant="ghost" className="h-6 w-6 ml-auto"
          onClick={() => {
            navigator.clipboard?.writeText(JSON.stringify(payload, null, 2));
            toast.success("Copied JSON");
          }}>
          <Copy className="h-3 w-3" />
        </Button>
      </div>
      <pre className="text-[10px] font-mono leading-relaxed p-3 bg-zinc-900 text-zinc-100 overflow-x-auto"
        data-testid="ato-sample-payload">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </div>
  );
}

/* Common section heading */
function SectionHead({ icon: Icon, title, sub }) {
  return (
    <div className="pb-2 border-b border-zinc-100">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-700" />
        <h2 className="font-display font-bold text-lg tracking-tight">{title}</h2>
      </div>
      {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  );
}
